import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

// material
import { withStyles } from '@material-ui/core/styles';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Tooltip,
} from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import SearchIcon from '@material-ui/icons/Search';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import IconButton from '@material-ui/core/IconButton';
import LinkIcon from '@material-ui/icons/Link';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

// ui
import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import FMPMonthYearPicker from '../../components/pureForm/FMPMonthYearPicker';
import InventoryReportsContainer from '../../containers/inventory/InventoryReports';

import { getSharedStyles } from '../../themes';
import FormComponent from '../../components/FormComponent';
import { search } from '../../modules/inventory/inventoryReports';
import { post } from '../../modules/utility';
import invoiceIcon from '../../images/invoicePdf.svg';
import ResourcePopup from '../../components/Dialogs/ResourcePopup';

const currentColumns = [
	{ displayId: 'terminalDisplay', label: 'Terminal', flex: 1.2 },
	{ displayId: 'supplierDisplay', label: 'Supplier', flex: 1 },
	{ displayId: 'railcarCountDisplay', label: 'Railcars', flex: 0.6 },
	{ displayId: 'deliveryCountDisplay', label: 'Deliveries', flex: 0.6 },
	{ displayId: 'gallonsDisplay', label: 'Gallons', flex: 2 },
	{ displayId: 'costDisplay', label: 'Cost', flex: 2 },
];

const styles = (theme) => ({
	...getSharedStyles(theme),
	bigButton: {
		borderRadius: '100px',
		padding: '18px 34px',
		fontSize: 16,
	},
	dateRange: {
		marginRight: 10,
	},
	summaryBar: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '0 24px',
		padding: '16px 20px',
		background: '#fff',
		borderRadius: 4,
		boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
		marginBottom: 16,
	},
	summaryBarStat: {
		minWidth: 140,
		marginBottom: 8,
	},
	summaryBarLabel: {
		fontSize: '0.7rem',
		color: '#888',
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
	},
	summaryBarValue: {
		fontSize: '1.4rem',
		fontWeight: 700,
		color: '#222',
		lineHeight: 1.2,
	},
	summaryBarDivider: {
		width: 1,
		background: '#e0e0e0',
		margin: '0 8px',
		alignSelf: 'stretch',
	},
	columnHeaderRow: {
		display: 'flex',
		alignItems: 'center',
		padding: '8px 64px 8px 24px',
		background: '#f5f5f5',
		borderBottom: '2px solid #ddd',
		borderRadius: '4px 4px 0 0',
		marginBottom: 0,
	},
	columnHeaderCell: {
		fontSize: '0.7rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#555',
		paddingRight: 8,
		minWidth: 0,
	},
	columnRow: {
		display: 'flex',
		alignItems: 'flex-start',
		flex: 1,
	},
	columnCell: {
		paddingRight: 8,
		fontSize: '0.875rem',
		textAlign: 'left',
		minWidth: 0,
	},
	columnLabel: {
		fontSize: '0.65rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#888',
		marginBottom: 2,
	},
	detailsSection: {
		width: '100%',
		flexDirection: 'column',
		overflow: 'hidden',
		boxSizing: 'border-box',
	},
	noRailcars: {
		color: '#888',
		fontStyle: 'italic',
		padding: 8,
	},
	linkedUnionRow: {
		borderLeft: '3px solid #1976d2',
		background: '#f5f9ff',
	},
	linkedBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		color: '#1976d2',
		fontSize: '0.7rem',
		fontWeight: 600,
		marginLeft: 6,
		verticalAlign: 'middle',
	},
	deliverySubRow: {
		background: '#fafafa',
	},
	deliveryTable: {
		fontSize: '0.8rem',
		background: '#f0f4f8',
		margin: '0 0 4px 0',
	},
	invoiceStatus: {
		fontSize: '0.75rem',
		fontWeight: 600,
		padding: '2px 6px',
		borderRadius: 10,
		display: 'inline-block',
	},
	expandToggle: {
		padding: 4,
	},
	inventorySummary: {
		display: 'flex',
		flexDirection: 'row',
		gap: 32,
		margin: 0,
		padding: 0,
	},
	inventorySummaryGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	inventorySummaryGroupHeader: {
		fontSize: '0.65rem',
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: '0.05em',
		color: '#888',
		marginBottom: 4,
	},
	inventorySummaryItem: {
		display: 'flex',
		fontSize: '0.85rem',
		color: '#333',
	},
	inventorySummaryItemTotal: {
		display: 'flex',
		fontSize: '0.85rem',
		fontWeight: 700,
		color: '#111',
		borderTop: '1px solid #ddd',
		paddingTop: 4,
		marginTop: 2,
	},
	inventorySummaryLabel: {
		color: '#888',
		minWidth: 140,
		textAlign: 'right',
		paddingRight: 8,
	},
	inventorySummaryCell: {
		color: '#222',
		fontFamily: 'monospace',
	},
	inventorySummaryCellNeg: {
		color: '#c62828',
		fontFamily: 'monospace',
	},
	inventorySummaryCellPos: {
		color: '#2e7d32',
		fontFamily: 'monospace',
	},
	railcarTableWrapper: {
		width: '100%',
		overflowX: 'auto',
		minWidth: 0,
	},
	calcNotesItem: {
		fontSize: '0.875rem',
		color: '#333',
		lineHeight: 1.6,
		paddingLeft: 16,
		marginBottom: 6,
		textAlign: 'left',
		'&::before': {
			content: '"•"',
			marginRight: 8,
			color: '#1976d2',
		},
	},
	pdfIcon: {
		display: 'inline-block',
		width: 14,
		height: 17,
		backgroundImage: `url('${invoiceIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
});

const isLocalhost = Boolean(window.location.hostname === 'localhost');

class InventoryReports extends FormComponent {
	constructor(props) {
		super(props);

		const dateRange = 'lastMonth';
		const { yearMonth } = this.dateRangeToDates(dateRange);
		this.state = {
			dateRange,
			yearMonth,
			yearMonthEnd: yearMonth,
			isSearching: false,
			terminalSupplierGroups: undefined,
			stationDeliveryMap: {},
			stationInvoiceMap: {},
			resourceMap: {},
			stationMap: {},
			terminalMap: {},
			railcarVendorBillMap: {},
			vendorBillMap: {},
			terminalFilterOptions: [],
			selectedTerminalIds: [],
			calculationNotes: [],
			expandedUnionKeys: new Set(),
			formFieldError: {},
		};
	}

	componentDidMount() {
		post({
			method: 'terminal.search',
			params: {},
			callback: (err, result) => {
				if (err) {
					return;
				}
				const terminalCollection = (result.collections || {}).terminalCollection || [];
				const terminalFilterOptions = _.sortBy(terminalCollection, (t) => t.name || '');
				const terminalMap = _.keyBy(terminalCollection, 'terminalId');
				const mhxFontana = terminalCollection.find((t) => (t.name || '').toLowerCase().includes('mhx fontana'));
				const selectedTerminalIds = mhxFontana ? [String(mhxFontana.terminalId)] : [];
				this.setState({ terminalFilterOptions, terminalMap, selectedTerminalIds });
			},
		});
	}

	dateRangeToDates = (dateRange) => {
		if (dateRange === 'thisMonth') {
			return { yearMonth: moment().startOf('month').format('YYYY-MM') };
		} else if (dateRange === 'lastMonth') {
			return { yearMonth: moment().subtract(1, 'month').startOf('month').format('YYYY-MM') };
		} else if (dateRange === 'twoMonthsAgo') {
			return { yearMonth: moment().subtract(2, 'months').startOf('month').format('YYYY-MM') };
		} else {
			return {};
		}
	};

	setDateRange = (dateRange) => {
		const { yearMonth } = this.dateRangeToDates(dateRange);
		if (!yearMonth) {
			this.setState({ dateRange });
		} else {
			this.setState({ dateRange, yearMonth, yearMonthEnd: yearMonth });
		}
	};

	toggleUnionExpanded = (unionKey) => {
		this.setState((prev) => {
			const next = new Set(prev.expandedUnionKeys);
			if (next.has(unionKey)) {
				next.delete(unionKey);
			} else {
				next.add(unionKey);
			}
			return { expandedUnionKeys: next };
		});
	};

	submitSearch = () => {
		const { showErrors } = this.props;
		const { yearMonth, yearMonthEnd, selectedTerminalIds } = this.state;

		this.setState({ isSearching: true });

		const afterDate = yearMonth
			? moment(yearMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD')
			: undefined;

		const beforeDate = yearMonthEnd
			? moment(yearMonthEnd, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
			: undefined;

		search({
			afterDate,
			beforeDate,
			terminalIds: selectedTerminalIds.length > 0 ? selectedTerminalIds : undefined,
			callback: (err, result) => {
				this.setState({ isSearching: false });
				if (err) {
					return showErrors(_.isArray(err) ? err : [err]);
				}

				const terminalSupplierGroups = result.terminalSupplierGroups || [];
				const calculationNotes = result.calculationNotes || [];

				const {
					stationDeliveryCollection,
					stationInvoiceCollection,
					stationCollection,
					terminalCollection,
					railcarVendorBillCollection,
					resourceCollection,
				} = result.collections;

				const newTerminalMap = _.keyBy(terminalCollection || [], 'terminalId');
				const terminalFilterOptions = _.sortBy(terminalCollection || [], (t) => t.name || '');

				this.setState((prev) => ({
					terminalSupplierGroups,
					stationDeliveryMap: _.keyBy(stationDeliveryCollection || [], 'stationDeliveryId'),
					stationInvoiceMap: _.keyBy(stationInvoiceCollection || [], 'linkedStationDeliveryId'),
					resourceMap: _.keyBy(resourceCollection || [], 'resourceId'),
					stationMap: _.keyBy(stationCollection || [], 'stationId'),
					terminalMap: newTerminalMap,
					railcarVendorBillMap: _.keyBy(railcarVendorBillCollection || [], 'linkedRailcarId'),
					vendorBillMap: _.keyBy(railcarVendorBillCollection || [], 'vendorBillOrderId'),
					terminalFilterOptions,
					selectedTerminalIds: prev.selectedTerminalIds,
					calculationNotes,
					expandedUnionKeys: new Set(),
				}));
			},
		});
	};

	renderDateButton = (label, range) => {
		const { classes } = this.props;
		const { dateRange } = this.state;
		const variant = dateRange === range ? 'contained' : 'outlined';

		return (
			<Button
				className={classes.dateRange}
				variant={variant}
				color="primary"
				onClick={() => this.setDateRange(range)}
			>
				{label}
			</Button>
		);
	};

	renderDateRangeOptions = () => {
		const { dateRange } = this.state;
		const isCustomRange = dateRange === 'custom';

		return (
			<>
				<Grid item md={12}>
					<FMPSectionHeader label={'Date Range'} isTop={true} />
				</Grid>

				<Grid item md={12} style={{ display: 'flex' }}>
					{this.renderDateButton('Current Month', 'thisMonth')}
					{this.renderDateButton('Last Month', 'lastMonth')}
					{this.renderDateButton('Two Months Prior', 'twoMonthsAgo')}
					{this.renderDateButton('Custom', 'custom')}
				</Grid>

				<FMPMonthYearPicker
					{...this.setForm('yearMonth', isCustomRange ? 'Start Month / Year' : 'Month / Year')}
					disabled={!isCustomRange}
					showFuture={true}
					md={6}
				/>

				{isCustomRange && (
					<FMPMonthYearPicker
						{...this.setForm('yearMonthEnd', 'End Month / Year')}
						disabled={false}
						showFuture={true}
						md={6}
					/>
				)}

				<Grid item md={12}>
					<FMPSectionHeader label={'Terminals'} />
				</Grid>
				<Grid item md={12}>
					{this.renderTerminalFilter()}
				</Grid>
			</>
		);
	};

	renderSearchOptions = () => {
		const { classes } = this.props;
		const { isSearching, yearMonth, yearMonthEnd, selectedTerminalIds } = this.state;
		const { accessToken } = this.props;

		return (
			<>
				<FMPHeadline label="Inventory Reports" />
				<Paper className={classes.paper}>
					<Grid container spacing={2}>
						{this.renderDateRangeOptions()}

						<Grid item md={12} style={{ paddingTop: 16, display: 'flex', alignItems: 'center' }}>
							<Button
								className={classes.bigButton}
								variant="contained"
								color="primary"
								onClick={this.submitSearch}
								disabled={isSearching}
							>
								<SearchIcon className={classes.icon} />
								Search
							</Button>

							{(() => {
								const afterDate = yearMonth ? `${yearMonth}-01` : undefined;
								const beforeDate = yearMonthEnd
									? moment(yearMonthEnd, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
									: undefined;
								const params = { accessToken, afterDate, beforeDate, terminalIds: selectedTerminalIds, downloadCsv: true };
								const request = { method: 'inventoryReports.search', params };
								let host = '';
								if (isLocalhost) {
									host = 'http://localhost:3700';
								}
								const downloadUrl = `${host}/api?request=${JSON.stringify(request)}`;
								return (
									<a
										href={downloadUrl}
										style={{ textDecoration: 'none', paddingLeft: 10 }}
										target="_blank"
										rel="noreferrer noopener"
									>
										<Button
											className={classes.bigButton}
											variant="contained"
											color="primary"
											disabled={isSearching}
										>
											<DownloadIcon className={classes.icon} />
											&nbsp;Download CSV
										</Button>
									</a>
								);
							})()}

							{isSearching && (
								<div className={classes.runningSearch} style={{ marginLeft: 16 }}>
									<CircularProgress />
									<span style={{ paddingLeft: 5 }}>Searching...</span>
								</div>
							)}
						</Grid>
					</Grid>
				</Paper>
			</>
		);
	};

	renderSummaryBar = (visibleGroups) => {
		const { classes } = this.props;

		const totalTerminals = visibleGroups.length;
		const totalRailcars = _.sumBy(visibleGroups, (g) =>
			_.sumBy(g.railcarUnions, (u) => u.railcars.length)
		);
		const totalDeliveries = _.sumBy(visibleGroups, (g) =>
			_.sumBy(g.railcarUnions, (u) => (u.linkedDeliveryLegs || []).filter((leg) => !leg.isNotInRange).length)
		);
		const gallons = _.sumBy(visibleGroups, 'soldGallons') || 0;

		const stat = (label, value) => (
			<div className={classes.summaryBarStat}>
				<div className={classes.summaryBarLabel}>{label}</div>
				<div className={classes.summaryBarValue}>{value}</div>
			</div>
		);

		return (
			<div className={classes.summaryBar}>
				{stat('Terminals', totalTerminals.toLocaleString())}
				<div className={classes.summaryBarDivider} />
				{stat('Railcars', totalRailcars.toLocaleString())}
				{stat('Deliveries', totalDeliveries.toLocaleString())}
				{stat('Net Gallons', gallons.toLocaleString())}
			</div>
		);
	};


	handlePdfClick = (resourceId) => {
		this.setState({
			resourceIdsPoppedUp: [resourceId],
			isResourcePopupOpen: true,
		});
	};

	handleCloseResourcePopup = () => {
		this.setState({
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
		});
	};

	formatBolPdfCell(bol, bolResourceId) {
		const { classes } = this.props;
		return (
			<div style={{ display: 'flex' }}>
				<div style={{ paddingRight: '5px' }}>{bol}</div>

				{bolResourceId ? (
					<div
						className={classes.pdfIcon}
						onClick={() => {
							this.handlePdfClick(bolResourceId);
						}}
					/>
				) : (
					<div className={classes.pdfIcon} style={{ opacity: '.3' }} />
				)}
			</div>
		);
	}

	renderDeliveryRows = (union) => {
		const { classes } = this.props;
		const { stationDeliveryMap, stationInvoiceMap, stationMap } = this.state;

		const isLinkedUnion = union.railcars.length > 1;

		const allLegs = union.linkedDeliveryLegs || [];
		const sortedLegs = _.sortBy(
			allLegs.filter((leg) => !leg.isNotInRange),
			(leg) => {
				const delivery = stationDeliveryMap[leg.stationDeliveryId];
				return delivery ? delivery.deliveryWindowStartDateTime : '';
			}
		);

		const outOfRangeCount = allLegs.filter((leg) => leg.isNotInRange).length;

		const unionVendorBills = union.vendorBills || [];

		const fmtDate = (d) => (d ? moment(d).format('M/D/YYYY') : '-');
		const formatNum = (val) => (val == null ? '-' : new Intl.NumberFormat('en-US').format(val));
		const formatCurrency = (val) =>
			val != null
				? `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
				: '-';

		const statusColor = (status) => {
			if (!status) return '#aaa';
			if (status === 'issued' || status === 'sent') return '#4CAF50';
			if (status === 'invalid') return '#f44336';
			return '#FF9800';
		};

		const subTableSectionLabel = (text) => (
			<div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#555', marginTop: 8, marginBottom: 2 }}>
				{text}
			</div>
		);





		return (
			<TableRow className={classes.deliverySubRow}>
				<TableCell colSpan={12} style={{ padding: '4px 8px 8px 48px' }}>
					{subTableSectionLabel('Deliveries')}
					<Table size="small" className={classes.deliveryTable}>
						<TableHead>
							<TableRow>
								{isLinkedUnion && <TableCell>Railcar</TableCell>}
								<TableCell>Station #</TableCell>
								<TableCell>Delivery ID</TableCell>
								<TableCell>Delivery Date</TableCell>
								<TableCell>Tank #</TableCell>
								<TableCell>Invoice #</TableCell>
								<TableCell>Invoice Date</TableCell>
								<TableCell>Invoice Status</TableCell>
								<TableCell>Invoice Total</TableCell>
								<TableCell>Invoiced Gal (sold)</TableCell>
								<TableCell>BOL Gal</TableCell>
								<TableCell>BOL</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{_.isEmpty(sortedLegs) && (
								<TableRow>
									<TableCell
										colSpan={isLinkedUnion ? 10 : 9}
										style={{ fontStyle: 'italic', color: '#888', fontSize: '0.75rem', textAlign: 'center', padding: '6px 8px' }}
									>
										{outOfRangeCount > 0
											? `${outOfRangeCount} ${outOfRangeCount === 1 ? 'delivery' : 'deliveries'} linked to this railcar but outside the date range`
											: 'No invoiced deliveries in this period'}
									</TableCell>
								</TableRow>
							)}
							{sortedLegs.map((leg) => {
								const delivery = stationDeliveryMap[leg.stationDeliveryId];
								const invoice = stationInvoiceMap[leg.stationDeliveryId];
								const deliveryDate = delivery ? fmtDate(delivery.deliveryWindowStartDateTime) : '-';
								const mapRailcar = isLinkedUnion
									? union.railcars.find((r) => r.railcarId === leg.railcarId)
									: null;
								const station = delivery ? stationMap[delivery.primaryStationId] : null;
								const stationNumber = station ? station.number : '-';

								return (
									<TableRow key={leg.stationDeliveryId}>
										{isLinkedUnion && (
											<TableCell>
												<span style={{ fontSize: '0.75rem', color: '#555' }}>
													{mapRailcar ? mapRailcar.railcarNumber : leg.railcarId}
												</span>
											</TableCell>
										)}
										<TableCell>
											<span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{stationNumber}</span>
										</TableCell>
										<TableCell>
											<span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
												{leg.stationDeliveryId}
												<a
													href={`/logistics/stationDelivery/update/${leg.stationDeliveryId}`}
													target="_blank"
													rel="noopener noreferrer"
													style={{ display: 'flex', alignItems: 'center', color: '#1976d2', marginLeft: 4 }}
												>
													<LinkIcon style={{ fontSize: '0.85rem' }} />
												</a>
											</span>
										</TableCell>
										<TableCell>{deliveryDate}</TableCell>
										<TableCell>{leg.tankNumber || '-'}</TableCell>

										<TableCell>
											{invoice ? (
												<a
													href={`/accounting/stationInvoice/update/${invoice.stationInvoiceId}`}
													target="_blank"
													rel="noopener noreferrer"
													style={{ color: '#1976d2', textDecoration: 'none' }}
												>
													{invoice.stationInvoiceId}
												</a>
											) : (
												<span style={{ color: '#aaa', fontStyle: 'italic' }}>No invoice</span>
											)}
										</TableCell>
										<TableCell>{invoice ? fmtDate(invoice.issueDate) : '-'}</TableCell>
										<TableCell>
											{invoice ? (
												<span
													className={classes.invoiceStatus}
													style={{
														background: statusColor(invoice.status) + '22',
														color: statusColor(invoice.status),
													}}
												>
													{invoice.status || '-'}
												</span>
											) : '-'}
										</TableCell>
										<TableCell>
											{invoice && invoice.total != null ? (
												leg.partialInvoiceTotalCost != null ? (
													<div>
														<div>{formatCurrency(leg.partialInvoiceTotalCost)}</div>
														<div style={{ fontSize: '0.7rem', color: '#aaa' }}>{formatCurrency(invoice.total)}</div>
													</div>
												) : (
													formatCurrency(invoice.total)
												)
											) : '-'}
										</TableCell>
										<TableCell>
											<span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
												{leg.invoicedNetGallonError && (
													<Tooltip title={<span style={{ fontSize: '0.85rem' }}>{leg.invoicedNetGallonError}</span>}>
														<WarningRoundedIcon style={{ color: '#f57c00', fontSize: 14, cursor: 'default' }} />
													</Tooltip>
												)}
												{formatNum(leg.invoicedNetGallons)}
											</span>
										</TableCell>
										<TableCell>{formatNum(leg.netGallons)}</TableCell>
										<TableCell>{this.formatBolPdfCell(leg.bol, leg.bolResourceId)}</TableCell>

									</TableRow>
								);
							})}
							{outOfRangeCount > 0 && !_.isEmpty(sortedLegs) && (
								<TableRow>
									<TableCell
										colSpan={isLinkedUnion ? 10 : 9}
										style={{ fontStyle: 'italic', color: '#888', fontSize: '0.75rem', textAlign: 'center', padding: '6px 8px' }}
									>
										{outOfRangeCount} {outOfRangeCount === 1 ? 'delivery' : 'deliveries'} linked to this railcar but outside the date range
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>

					{subTableSectionLabel('Vendor Bills')}
					{_.isEmpty(unionVendorBills) ? (
						<div className={classes.noRailcars} style={{ marginBottom: 4 }}>No vendor bills linked to this railcar</div>
					) : (
						<Table size="small" className={classes.deliveryTable}>
							<TableHead>
								<TableRow>
									{isLinkedUnion && <TableCell>Railcar</TableCell>}
									<TableCell>Bill #</TableCell>
									<TableCell>Bill Date</TableCell>
									<TableCell>Supplier</TableCell>
									<TableCell>BOL #</TableCell>
									<TableCell>Net Gal</TableCell>
									<TableCell>Order Cost</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{unionVendorBills.map((vb) => {
									const vbRailcar = isLinkedUnion
										? union.railcars.find((r) => r.railcarId === vb.linkedRailcarId)
										: null;
									return (
										<TableRow key={vb.vendorBillOrderId}>
											{isLinkedUnion && (
												<TableCell>
													<span style={{ fontSize: '0.75rem', color: '#555' }}>
														{vbRailcar ? vbRailcar.railcarNumber : vb.linkedRailcarId}
													</span>
												</TableCell>
											)}
											<TableCell>
												<a
													href={`/billing/vendorBill/update/${vb.vendorBillId}`}
													target="_blank"
													rel="noopener noreferrer"
													style={{ color: '#1976d2', textDecoration: 'none' }}
												>
													{vb.billNumber || vb.vendorBillId}
												</a>
											</TableCell>
											<TableCell>{vb.billDate ? fmtDate(vb.billDate) : '-'}</TableCell>
											<TableCell>{vb.supplierName || '-'}</TableCell>
											<TableCell>{vb.bolNumber || '-'}</TableCell>
											<TableCell>{formatNum(vb.netGallons)}</TableCell>
											<TableCell>{formatCurrency(vb.totalCost)}</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</TableCell>
			</TableRow>
		);
	};

	renderInventorySummary = (group) => {
		const { classes } = this.props;

		const {
			beginningInventoryGallons,
			beginningInventoryCost,
			purchasedGallons,
			purchasedCost,
			soldGallons,
			soldCost,
			gainLossForTimeFrame,
			gainLossCost,
			endingInventoryGallons,
			endingInventoryCost,
			purchasedGallonsWarning = null,
		} = group;

		const fmtGal = (n) => {
			if (n == null) return '-';
			const abs = Math.round(Math.abs(n));
			const fmt = new Intl.NumberFormat('en-US').format(abs);
			return n < 0 ? `(${fmt})` : fmt;
		};
		const galClass = (n) =>
			n < 0 ? classes.inventorySummaryCellNeg : n > 0 ? classes.inventorySummaryCellPos : '';

		const rows = [
			{ label: 'Beg. Inventory', gallons: beginningInventoryGallons, cost: beginningInventoryCost },
			{ label: 'Purchased', gallons: purchasedGallons, cost: purchasedCost, warning: purchasedGallonsWarning },
			{ label: 'Sold', gallons: -soldGallons, cost: -soldCost },
			{ label: 'Gain / Loss', gallons: gainLossForTimeFrame, cost: gainLossCost },
			{ label: 'End. Inventory', gallons: endingInventoryGallons, cost: endingInventoryCost, isTotal: true },
		];

		const gallonsDisplay = (
			<div className={classes.inventorySummaryGroup}>
				{rows.map((row) => (
					<div key={row.label} className={row.isTotal ? classes.inventorySummaryItemTotal : classes.inventorySummaryItem}>
						<span className={classes.inventorySummaryLabel} style={{ display: 'inline-flex', alignItems: 'center' }}>
							{row.label}:
							{row.warning && (
								<Tooltip title={<span style={{ fontSize: '0.85rem' }}>{row.warning}</span>}>
									<WarningRoundedIcon style={{ color: '#f57c00', marginLeft: 4, fontSize: 14, cursor: 'default' }} />
								</Tooltip>
							)}
						</span>
						<span className={galClass(row.gallons) || classes.inventorySummaryCell}>{fmtGal(row.gallons)}</span>
					</div>
				))}
			</div>
		);

		const costDisplay = (
			<div className={classes.inventorySummaryGroup}>
				{rows.map((row) => (
					<div key={row.label} className={row.isTotal ? classes.inventorySummaryItemTotal : classes.inventorySummaryItem}>
						<span className={classes.inventorySummaryLabel}>{row.label}:</span>
						<span className={classes.inventorySummaryCell}>-</span>
					</div>
				))}
			</div>
		);

		return { gallonsDisplay, costDisplay };
	};

	renderRailcarTable = (railcarUnionsWithDeliveries) => {
		const { classes } = this.props;
		const { expandedUnionKeys } = this.state;

		if (_.isEmpty(railcarUnionsWithDeliveries)) {
			return <div className={classes.noRailcars}>No railcars matched this contract</div>;
		}

		const fmtDate = (d) => (d ? moment(d).format('MMM-D') : '-');
		const formatNum = (val) => (val == null ? '-' : new Intl.NumberFormat('en-US').format(val));
		//const fmtCost = (n) => {
		//	if (n == null) return <span style={{ color: '#aaa', fontStyle: 'italic' }}>No bill</span>;
		//	return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
		//};

		const getContractAgeStyle = (railcar) => {
			if (railcar.contractInCurrentTimeFrame) {
				return { backgroundColor: '#e8f5e9' };
			}
			if (railcar.monthsContractOutsideCurrentTimeFrame === 1) {
				return { backgroundColor: '#fffde7' };
			}
			if (railcar.monthsContractOutsideCurrentTimeFrame >= 2) {
				return { backgroundColor: '#fff3e0' };
			}
			return {};
		};

		/*
		const getVendorBillCell = (railcar) => {
			const bill = railcarVendorBillMap[railcar.railcarId];
			if (!bill) {
				if (railcar.isForThisSupplier === false) {
					return '-';
				}
				return <span style={{ color: '#e65100', fontWeight: 500 }}>Missing</span>;
			}
			return (
				<a
					href={`/billing/vendorBill/update/${bill.vendorBillId}`}
					target="_blank"
					rel="noopener noreferrer"
					style={{ color: '#1976d2', textDecoration: 'none', fontSize: '0.8rem' }}
				>
					{bill.billNumber || bill.vendorBillId}
				</a>
			);
		};
		*/

		const getPurchaseGallonsCell = (railcar) => {
			if (!railcar.purchasedGallons) {
				if (railcar.isForThisSupplier === false) {
					return '-';
				}
				return <span style={{ color: '#e65100', fontWeight: 500 }}>Missing</span>;
			}
			return formatNum(railcar.purchasedGallons);
		};

		return (
			<div className={classes.railcarTableWrapper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell style={{ width: 40 }}>Del</TableCell>
							<TableCell>Railcar #</TableCell>
							<TableCell>Contract #</TableCell>
							<TableCell>Supplier / Product</TableCell>
							<TableCell>PO #</TableCell>
							<TableCell>CP / AP Date</TableCell>
							<TableCell>Empty <br /> Rel Date</TableCell>
							<TableCell>Purchased<br />Gallons</TableCell>
							<TableCell>Sold<br />Gallons</TableCell>
							<TableCell>BOL<br />Gallons</TableCell>
							<TableCell>Railcar<br />Gallons</TableCell>
							<TableCell>Gain/Loss<br />Gallons</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{railcarUnionsWithDeliveries.map((union) => {
							const primaryRailcar = union.railcars[0];
							const unionKey = primaryRailcar.railcarId;
							const isLinked = union.railcars.length > 1;
							const isExpanded = expandedUnionKeys.has(unionKey);
							const allLegs = union.linkedDeliveryLegs || [];
							const deliveryCount = allLegs.filter((leg) => !leg.isNotInRange).length;
							const hasAnyDeliveries = allLegs.length > 0;

							if (isLinked) {
								return (
									<React.Fragment key={unionKey}>
										{union.railcars.map((railcar, idx) => {
											const poDisplay = railcar.requestedMonth
												? `${moment(railcar.requestedMonth).format('MMM')} (#${railcar.requestedNumber || '-'})`
												: '-';
											const railcarLegErrors = allLegs
												.filter((leg) => leg.railcarId === railcar.railcarId && leg.invoicedNetGallonError)
												.map((leg) => leg.invoicedNetGallonError);
											return (
												<TableRow key={railcar.railcarId} className={classes.linkedUnionRow} style={{ ...getContractAgeStyle(railcar), ...(railcar.isForThisSupplier === false ? { backgroundColor: '#f5f5f5', color: '#aaa' } : {}) }}>
													{idx === 0 && (
														<TableCell rowSpan={union.railcars.length} style={{ verticalAlign: 'middle', textAlign: 'center' }}>
															<IconButton
																className={classes.expandToggle}
																size="small"
																onClick={() => this.toggleUnionExpanded(unionKey)}
																disabled={!hasAnyDeliveries}
															>
																{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
															</IconButton>
															<div style={{ fontSize: '0.8em', color: deliveryCount > 0 ? '#1976d2' : '#aaa', fontWeight: deliveryCount > 0 ? 600 : 400, lineHeight: 1 }}>
																{deliveryCount}
															</div>
														</TableCell>
													)}
													<TableCell>
														<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
															{idx > 0 && <LinkIcon style={{ fontSize: 12, color: '#1976d2' }} />}
															{railcar.isSplitUnion && <CallSplitIcon style={{ fontSize: 14, color: '#1976d2' }} titleAccess="split between suppliers" />}
															{railcar.railcarNumber || '-'}
														</span>
													</TableCell>
													<TableCell>{railcar.fuelContractId || '-'}</TableCell>
													<TableCell>
														<div>{railcar.fuelSupplier || '-'}</div>
														<div style={{ fontSize: '0.75rem', color: '#666' }}>{railcar.product || '-'}</div>
													</TableCell>
													<TableCell>{poDisplay}</TableCell>
													<TableCell>
														<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>CP:</span> {fmtDate(railcar.cpDate)}</div>
														<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>AP:</span> {fmtDate(railcar.apDate)}</div>
													</TableCell>
													<TableCell>
														<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>Emp:</span> {fmtDate(railcar.emptyDate)}</div>
														<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>Rel:</span> {fmtDate(railcar.relinquishedDate)}</div>
													</TableCell>
													<TableCell>{getPurchaseGallonsCell(railcar)}</TableCell>
													<TableCell>
														<span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
															{railcarLegErrors.length > 0 && (
																<Tooltip title={<span style={{ fontSize: '0.85rem' }}><ul style={{ margin: 0, paddingLeft: 16 }}>{railcarLegErrors.map((err, i) => <li key={i}>{err}</li>)}</ul></span>}>
																	<WarningRoundedIcon style={{ color: '#f57c00', fontSize: 14, cursor: 'default' }} />
																</Tooltip>
															)}
															{railcar.soldGallons != null ? formatNum(railcar.soldGallons) : '-'}
														</span>
													</TableCell>
													<TableCell>{formatNum(railcar.netGallonsOutForTimeFrame)}</TableCell>
													<TableCell>{formatNum(railcar.netGallons)}</TableCell>
													<TableCell style={{ color: railcar.gainLossForTimeFrame < 0 ? '#c62828' : undefined }}>
														{formatNum(railcar.gainLossForTimeFrame)}
													</TableCell>
												</TableRow>
											);
										})}
										{isExpanded && this.renderDeliveryRows(union)}
									</React.Fragment>
								);
							}

							const poDisplay = primaryRailcar.requestedMonth
								? `${moment(primaryRailcar.requestedMonth).format('MMM')} (#${primaryRailcar.requestedNumber || '-'})`
								: '-';
							const primaryLegErrors = allLegs
								.filter((leg) => leg.invoicedNetGallonError)
								.map((leg) => leg.invoicedNetGallonError);

							return (
								<React.Fragment key={unionKey}>
									<TableRow style={{ ...getContractAgeStyle(primaryRailcar), ...(primaryRailcar.isForThisSupplier === false ? { backgroundColor: '#f5f5f5', color: '#aaa' } : {}) }}>
										<TableCell style={{ textAlign: 'center' }}>
											<IconButton
												className={classes.expandToggle}
												size="small"
												onClick={() => this.toggleUnionExpanded(unionKey)}
												disabled={!hasAnyDeliveries}
											>
												{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
											</IconButton>
											<div style={{ fontSize: '0.8em', color: deliveryCount > 0 ? '#1976d2' : '#aaa', fontWeight: deliveryCount > 0 ? 600 : 400, lineHeight: 1 }}>
												{deliveryCount}
											</div>
										</TableCell>
										<TableCell>
											<span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
												{primaryRailcar.isSplitUnion && <CallSplitIcon style={{ fontSize: 14, color: '#1976d2' }} titleAccess="split between suppliers" />}
												{primaryRailcar.railcarNumber || '-'}
											</span>
										</TableCell>
										<TableCell>{primaryRailcar.fuelContractId || '-'}</TableCell>
										<TableCell>
											<div>{primaryRailcar.fuelSupplier || '-'}</div>
											<div style={{ fontSize: '0.75rem', color: '#666' }}>{primaryRailcar.product || '-'}</div>
										</TableCell>
										<TableCell>{poDisplay}</TableCell>
										<TableCell>
											<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>CP:</span> {fmtDate(primaryRailcar.cpDate)}</div>
											<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>AP:</span> {fmtDate(primaryRailcar.apDate)}</div>
										</TableCell>
										<TableCell>
											<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>Emp:</span> {fmtDate(primaryRailcar.emptyDate)}</div>
											<div><span style={{ color: '#888', fontSize: '0.7rem', fontWeight: 600 }}>Rel:</span> {fmtDate(primaryRailcar.relinquishedDate)}</div>
										</TableCell>
										<TableCell>{getPurchaseGallonsCell(primaryRailcar)}</TableCell>
										<TableCell>
											<span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
												{primaryLegErrors.length > 0 && (
													<Tooltip title={<span style={{ fontSize: '0.85rem' }}><ul style={{ margin: 0, paddingLeft: 16 }}>{primaryLegErrors.map((err, i) => <li key={i}>{err}</li>)}</ul></span>}>
														<WarningRoundedIcon style={{ color: '#f57c00', fontSize: 14, cursor: 'default' }} />
													</Tooltip>
												)}
												{primaryRailcar.soldGallons != null ? formatNum(primaryRailcar.soldGallons) : '-'}
											</span>
										</TableCell>
										<TableCell>{formatNum(primaryRailcar.netGallonsOutForTimeFrame)}</TableCell>
										<TableCell>{formatNum(primaryRailcar.netGallons)}</TableCell>
										<TableCell style={{ color: primaryRailcar.gainLossForTimeFrame < 0 ? '#c62828' : undefined }}>
											{formatNum(primaryRailcar.gainLossForTimeFrame)}
										</TableCell>
									</TableRow>
									{isExpanded && this.renderDeliveryRows(union)}
								</React.Fragment>
							);
						})}
					</TableBody>
				</Table>
			</div>
		);
	};

	renderColumnHeader = () => {
		const { classes } = this.props;
		return (
			<div className={classes.columnHeaderRow}>
				{currentColumns.map((col) => (
					<div
						key={col.displayId}
						className={classes.columnHeaderCell}
						style={{ flex: col.flex || 1 }}
					>
						{col.labels ? col.labels.join(' / ') : col.label}
					</div>
				))}
			</div>
		);
	};

	renderGroupRow = (group) => {
		const { classes } = this.props;
		const { terminalMap } = this.state;

		const { terminalId, fuelSupplier, railcarUnions } = group;
		const terminal = terminalMap[terminalId] || {};

		const totalRailcars = _.sumBy(railcarUnions, (u) => u.railcars.length);
		const totalDeliveries = _.sumBy(railcarUnions, (u) =>
			(u.linkedDeliveryLegs || []).filter((leg) => !leg.isNotInRange).length
		);

		const { gallonsDisplay, costDisplay } = this.renderInventorySummary(group);

		const displayProps = {
			terminalDisplay: <div>{terminal.name || terminalId || '-'}</div>,
			supplierDisplay: <div>{fuelSupplier || '-'}</div>,
			railcarCountDisplay: <div>{totalRailcars}</div>,
			deliveryCountDisplay: <div>{totalDeliveries}</div>,
			gallonsDisplay,
			costDisplay,
		};

		return (
			<Accordion key={`${terminalId}_${fuelSupplier || ''}`} defaultExpanded style={{ marginBottom: 8, overflow: 'hidden' }}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<div className={classes.columnRow}>
						{currentColumns.map((col) => (
							<div
								key={col.displayId}
								className={classes.columnCell}
								style={{ flex: col.flex || 1 }}
							>
								<div className={classes.columnLabel}>
									{col.labels ? col.labels.join(' / ') : col.label}
								</div>
								{displayProps[col.displayId]}
							</div>
						))}
					</div>
				</AccordionSummary>
				<AccordionDetails className={classes.detailsSection}>
					{this.renderRailcarTable(railcarUnions)}
				</AccordionDetails>
			</Accordion>
		);
	};

	renderTerminalFilter = () => {
		const { terminalFilterOptions, selectedTerminalIds, terminalMap } = this.state;

		const handleChange = (e) => {
			this.setState({ selectedTerminalIds: e.target.value });
		};

		const renderValue = (selected) => {
			if (selected.length === 0) return 'All terminals';
			return selected
				.map((id) => (terminalMap[id] ? terminalMap[id].name : `Terminal ${id}`))
				.join(', ');
		};

		return (
			<FormControl variant="outlined" size="small" style={{ minWidth: 280, marginBottom: 16 }}>
				<InputLabel>Terminal Filter</InputLabel>
				<Select
					multiple
					value={selectedTerminalIds}
					onChange={handleChange}
					label="Terminal Filter"
					renderValue={renderValue}
				>
					{terminalFilterOptions.map((t) => (
						<MenuItem key={t.terminalId} value={String(t.terminalId)}>
							<Checkbox checked={selectedTerminalIds.includes(String(t.terminalId))} />
							<ListItemText primary={t.name || `Terminal ${t.terminalId}`} />
						</MenuItem>
					))}
				</Select>
			</FormControl>
		);
	};

	renderCalculationNotes = () => {
		const { classes } = this.props;
		const { calculationNotes } = this.state;

		if (_.isEmpty(calculationNotes)) {
			return null;
		}

		return (
			<Accordion style={{ marginBottom: 16 }}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#444', textAlign: 'left' }}>
						How These Numbers Are Calculated
					</span>
				</AccordionSummary>
				<AccordionDetails style={{ flexDirection: 'column', padding: '0 24px 16px', alignItems: 'flex-start' }}>
					{calculationNotes.map((note, i) => (
						<div key={i} className={classes.calcNotesItem}>
							{note}
						</div>
					))}
				</AccordionDetails>
			</Accordion>
		);
	};

	renderResults = () => {
		const { classes } = this.props;
		const { terminalSupplierGroups, isSearching, selectedTerminalIds } = this.state;

		if (terminalSupplierGroups === undefined) {
			return (
				<div style={{ color: 'gray', fontStyle: 'italic' }}>
					Click search to see inventory reports
				</div>
			);
		}

		if (isSearching) {
			return (
				<div className={classes.runningSearch}>
					<CircularProgress />
					<span style={{ paddingLeft: 5 }}>Searching...</span>
				</div>
			);
		}

		if (_.isEmpty(terminalSupplierGroups)) {
			return <div style={{ color: 'gray', fontStyle: 'italic' }}>No terminals found</div>;
		}

		const visibleGroups =
			selectedTerminalIds.length === 0
				? terminalSupplierGroups
				: terminalSupplierGroups.filter((g) =>
					selectedTerminalIds.includes(String(g.terminalId))
				);

		return (
			<div>
				{this.renderSummaryBar(visibleGroups)}
				{this.renderColumnHeader()}
				{visibleGroups.map((group) => this.renderGroupRow(group))}
			</div>
		);
	};



	render() {
		const { canRwContracts } = this.props;

		if (!canRwContracts) {
			return <div>Permission denied</div>;
		}

		return (
			<Grid container spacing={2}>
				<Grid item md={12} xs={12}>
					{this.renderSearchOptions()}
				</Grid>
				<Grid item md={12} xs={12}>
					{this.renderCalculationNotes()}
				</Grid>
				<Grid item md={12} xs={12}>
					{this.renderResults()}
				</Grid>

				<ResourcePopup
					open={this.state.isResourcePopupOpen}
					onClose={this.handleCloseResourcePopup}
					resourceIds={this.state.resourceIdsPoppedUp}
					resourceMap={this.state.resourceMap}
				/>

			</Grid>
		);
	}
}

InventoryReports.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
	canRwContracts: PropTypes.bool,
	history: PropTypes.object,
};

export default _.flow([withStyles(styles, { withTheme: true }), InventoryReportsContainer])(
	InventoryReports
);
